// Pure maths for the mortgage scenario calculator. Ported 1:1 from the source
// at elinswholesale.co.uk/elinsconsultancycalculator so the numbers match the
// client's existing tool exactly.
//
// 2026/27 tax year — England, Wales and Northern Ireland. PAYE, employee NI
// Category A, and residential SDLT (England / NI individuals).

export const fmt = new Intl.NumberFormat("en-GB", {
  style: "currency",
  currency: "GBP",
  maximumFractionDigits: 2,
});
export const cash = (n) => fmt.format(Number.isFinite(n) ? n : 0);

export const tax = (g) => {
  const pa = Math.max(0, 12570 - Math.max(0, g - 100000) / 2);
  const t = Math.max(0, g - pa);
  return (
    Math.min(t, 37700) * 0.2 +
    Math.min(Math.max(0, t - 37700), 87440) * 0.4 +
    Math.max(0, t - 125140) * 0.45
  );
};

export const ni = (g) =>
  Math.min(Math.max(0, g / 12 - 1048), 3141) * 0.08 +
  Math.max(0, g / 12 - 4189) * 0.02;

export const payment = (p, r, y) =>
  r === 0 ? p / (y * 12) : (p * (r / 12)) / (1 - Math.pow(1 + r / 12, -y * 12));

export const sdlt = (price, buyer, nonresident, eligible) => {
  const higher = ["additional", "pending"].includes(buyer) && price >= 40000;
  const first = buyer === "first" && eligible && price <= 500000;
  const base = first
    ? Math.max(0, price - 300000) * 0.05
    : Math.min(Math.max(0, price - 125000), 125000) * 0.02 +
      Math.min(Math.max(0, price - 250000), 675000) * 0.05 +
      Math.min(Math.max(0, price - 925000), 575000) * 0.1 +
      Math.max(0, price - 1500000) * 0.12;
  return (
    base +
    (higher ? price * 0.05 : 0) +
    (nonresident && price >= 40000 ? price * 0.02 : 0)
  );
};

export const BUYER_OPTIONS = [
  { value: "standard", label: "Only home / previous owner — standard rates" },
  { value: "first", label: "First-time buyer — eligible own home" },
  { value: "replacement", label: "Replacing main home — previous home already sold" },
  { value: "additional", label: "Additional property / second home / buy-to-let" },
  { value: "pending", label: "Replacing main home — previous home not yet sold" },
];

export const HOUSING_OPTIONS = [
  "Owner with mortgage",
  "Owner outright",
  "Private tenant",
  "Council / housing association tenant",
  "Living with parents / family",
  "Living with friends",
  "Lodger / boarding",
  "Employer-provided accommodation",
  "Temporary accommodation",
  "Other",
];

export const FEE_ITEMS = [
  { key: "valuation", label: "Lender valuation" },
  { key: "survey", label: "Independent property survey" },
  { key: "solicitor", label: "Solicitor / conveyancer" },
  { key: "searches", label: "Searches & legal disbursements" },
  { key: "product", label: "Mortgage product / arrangement fee" },
  { key: "broker", label: "Broker / consultancy fee", percent: true },
  { key: "registry", label: "Land Registry fee" },
  { key: "moving", label: "Moving costs" },
  { key: "otherfee", label: "Other purchase costs" },
];

export const DEFAULTS = {
  applicationType: "single",
  incomeMode: "auto",
  incomeSplit: 50,
  price: 400000,
  deposit: 15,
  rate: 5.97,
  term: 30,
  multiple: 5,
  income1: 68000,
  income2: "",
  deductions: "",
  costs: "",
  buyer: "standard",
  eligible: false,
  nonresident: false,
};

export const buyerHelp = (buyer, eligible, price) => {
  if (buyer === "first")
    return !eligible
      ? "Confirm eligibility to apply first-time buyer relief. Standard rates shown until confirmed."
      : price > 500000
      ? "Price exceeds £500,000: first-time buyer relief is unavailable; standard rates apply."
      : "First-time relief applied: 0% to £300,000, then 5% to £500,000.";
  if (buyer === "pending")
    return "Higher rates are budgeted at completion. A refund of the additional 5% may be available if the previous main home is sold within 3 years and HMRC conditions are met.";
  if (buyer === "replacement")
    return "Standard rates assumed only if the previous main home was sold within 3 years and the replacement conditions are satisfied.";
  if (buyer === "additional")
    return "Usually standard rates plus 5%. Overseas property, joint buyers and spouses can affect eligibility; verify with your conveyancer.";
  return "Standard residential rates apply. A first rental property does not qualify for first-time buyer own-home relief.";
};
